import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { useUiStore } from '@/store/ui';
import { getUrlParamsFetch, handleAxiosError, toApiError } from '@/shared';
import { ISolicitudesEgresoPagingRes, SolicitudesEgreso } from '@/shared/interfaces/erp/compras/solicitudes-egreso.interface';
import { AlertSeverityEnum, UseMutationsParams } from '@/shared/interfaces';

const { get, post, put } = erpApi();


export const useFetchSolicitudesEgreso = (params?: GetSolicitudesEgresosParams) => {
  return useQuery({
    queryKey: ['solicitud-egresos', ...Object.values(params || {})],
    queryFn: async () => {
      return GetSolicitudesEgresos(params);
    },
  });
};
export const useGetSolicitudPedido = (id: string) => {
  return useQuery({
    queryKey: ['solicitud-egresos', id],
    queryFn: async () => {
      return getSolicitudesEgreso(id);
    },
  });
};

export const useCreateSolicitudesEgresos = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createSolicitudesEgreso,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['solicitud-egresos'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Solicitud egreso creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};
export const useUpdateSolicitudesEgresos = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateSolicitudesEgreso,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['solicitud-egresos'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Solicitud egreso actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};
export const useRemoveSolicitudesEgresos = () => {
  const queryClient = useQueryClient();
  const setConfirmDialogIsOpen = useUiStore.getState().setConfirmDialogIsOpen;
  return useMutation({
    mutationFn: deleteSolicitudesEgreso,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['solicitud-egresos'] });
    },
    onSettled: () => {
      setConfirmDialogIsOpen(false);
    },
    onError: (error) => {
      handleAxiosError(error);
    },
  });
};

///* axios
export type GetSolicitudesEgresosParams = {
  page?: number;
  page_size?: number;
  nombre?: string;
  numero_referencia?: string;
  estado_solicitud?: string;
  fecha_after?: string;
  fecha_before?: string;
  //*
  url_pdf?: string | null
};

export type CreateSolicitudesEgresosParams = Omit<SolicitudesEgreso, 'id_solicitudegreso'>;
export type UpdateSolicitudesEgresosParams = {
  id: string;
  data: CreateSolicitudesEgresosParams;
};


export const GetSolicitudesEgresos = async (params?: GetSolicitudesEgresosParams) => {
  const queryParams = getUrlParamsFetch({ ...params });

  return get<ISolicitudesEgresoPagingRes>(
    `${urlBackend}api/v1/solicitud-egreso/?${queryParams}`,
    true
  );
};
export const getSolicitudesEgreso = async (id: string) => {
  try {
    return await get<SolicitudesEgreso>(`${urlBackend}api/v1/solicitud-egreso/${id}/`, true);
  }
  catch (error) {
    return handleAxiosError(error);
  }
};

export const createSolicitudesEgreso = async (data: CreateSolicitudesEgresosParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<SolicitudesEgreso>(`${urlBackend}api/v1/solicitud-egreso/`, data, true);
};

export const updateSolicitudesEgreso = async ({ id, data }: UpdateSolicitudesEgresosParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);
  return put<SolicitudesEgreso>(`${urlBackend}api/v1/solicitud-egreso/${id}/`, data, true);
};

export const deleteSolicitudesEgreso = async ({ id, data }: UpdateSolicitudesEgresosParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<SolicitudesEgreso>(`${urlBackend}api/v1/solicitud-egreso/${id}/`, data, true);
  setAlert(true, res.message, res.status);
  return res;
};


///PDF
export const useFetchPutSolicitudPedito = () => {
    console.log("esta en el fetch")
    const queryClient = useQueryClient();
    const setAlert = useUiStore.getState().setAlert;
    return useMutation({
          mutationFn: putSolicitudEgreso,
          onSuccess: data => {
                queryClient.invalidateQueries({ queryKey: ['solicitud-egreso'] });
                setAlert(true, data.message, data.status);
              },
          onError: error => {
                const cleanError = toApiError(error);
                handleAxiosError(cleanError)
              }
    });
}


export const putSolicitudEgreso = async ({ id, forzar = true}: { id: string, forzar?:boolean }) => {
  const resp = await post<SolicitudesEgreso>(`${urlBackend}api/v1/solicitud-egreso/${id}/pdf/${forzar ? '?forzar=true' : ''}`,{}, true);
  return resp;
};


///
export const postCorreoExcedente = async (
  body: { email: string; asunto: string; mensaje: string }
) => {
  return await post(
    `${urlBackend}api/v1/solicitud-egreso/correo-excedente/`,
    body,
    true // autenticado
  );
};
////PARAMETROS

export const getParametroPorCodigo = async (codigo: string) => {
  return await get<{ data: any[]; count?: number }>(
    `${urlBackend}api/v1/parametro/?codigo=${codigo}&page_size=1`,
    true
  );
};

export const getParametrosMetros = async () => {
  const [maxRes, minRes] = await Promise.all([
    getParametroPorCodigo("METROS_MAX_EXCEDENTE"),
    getParametroPorCodigo("METROS_MIN_FALTANTE"),
  ]);
  const maxExcedente = Number(maxRes?.data?.data?.[0]?.valor ?? 0) || 0;
  const minFaltante = Number(minRes?.data?.data?.[0]?.valor ?? 0) || 0;
  return { maxExcedente, minFaltante };
};